import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, FormControlLabel, Switch, Alert, Stack, MenuItem } from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';
import { customerService } from '../../services/customer.service';
import { Customer } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
import groupsService from '../../services/groups.service';

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);
  const [customer, setCustomer] = useState<Partial<Customer & { customerGroupId: number | null }>>({
    code: '', name: '', contactPerson: '', email: '', phone: '', address: '', city: '', country: '',
    taxNumber: '', customerType: 'BUSINESS', paymentTerms: 'NET30', creditLimit: 0, customerGroupId: null, isActive: true,
  });

  useEffect(() => {
    loadGroups();
    if (id) loadCustomer();
  }, [id]);

  const loadGroups = async () => {
    try { const res = await groupsService.getCustomerGroups(); setCustomerGroups(res.data); } catch { }
  };

  const loadCustomer = async () => {
    try { const data = await customerService.getById(Number(id)); setCustomer(data); }
    catch (error) { setError('Failed to load customer'); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (id) await customerService.update(Number(id), customer);
      else await customerService.create(customer);
      navigate('/customers');
    } catch (err: any) { setError(err.response?.data?.message || 'Failed to save customer'); }
    finally { setLoading(false); }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>{id ? t('customers.edit') : t('customers.new')}</Typography>
      <Paper sx={{ p: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('customers.customer_code')} value={customer.code}
                onChange={e => setCustomer({ ...customer, code: e.target.value })} disabled={loading} required />
              <TextField fullWidth label={t('customers.customer_name')} value={customer.name}
                onChange={e => setCustomer({ ...customer, name: e.target.value })} disabled={loading} required />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth select label={t('customers.customer_type')} value={customer.customerType}
                onChange={e => setCustomer({ ...customer, customerType: e.target.value })} disabled={loading}>
                <MenuItem value="BUSINESS">{t('customers.business')}</MenuItem>
                <MenuItem value="INDIVIDUAL">{t('customers.individual')}</MenuItem>
              </TextField>
              <TextField fullWidth select label={t('common.payment_terms')} value={customer.paymentTerms}
                onChange={e => setCustomer({ ...customer, paymentTerms: e.target.value })} disabled={loading}>
                <MenuItem value="NET30">{t('customers.net30')}</MenuItem>
                <MenuItem value="NET60">{t('customers.net60')}</MenuItem>
                <MenuItem value="NET90">{t('customers.net90')}</MenuItem>
                <MenuItem value="COD">{t('customers.cod')}</MenuItem>
              </TextField>
              <TextField fullWidth select label={t('customers.customer_group')} value={customer.customerGroupId ?? ''}
                onChange={e => setCustomer({ ...customer, customerGroupId: e.target.value ? Number(e.target.value) : null })} disabled={loading}>
                <MenuItem value="">{t('common.none')}</MenuItem>
                {customerGroups.map((g: any) => <MenuItem key={g.id} value={g.id}>{g.code} - {g.description}</MenuItem>)}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('common.contact_person')} value={customer.contactPerson}
                onChange={e => setCustomer({ ...customer, contactPerson: e.target.value })} disabled={loading} />
              <TextField fullWidth label={t('common.email')} type="email" value={customer.email}
                onChange={e => setCustomer({ ...customer, email: e.target.value })} disabled={loading} required />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('common.phone')} value={customer.phone}
                onChange={e => setCustomer({ ...customer, phone: e.target.value })} disabled={loading} />
              <TextField fullWidth label={t('common.tax_number')} value={customer.taxNumber}
                onChange={e => setCustomer({ ...customer, taxNumber: e.target.value })} disabled={loading} />
            </Box>
            <TextField fullWidth label={t('common.address')} value={customer.address}
              onChange={e => setCustomer({ ...customer, address: e.target.value })} disabled={loading} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField fullWidth label={t('common.city')} value={customer.city}
                onChange={e => setCustomer({ ...customer, city: e.target.value })} disabled={loading} />
              <TextField fullWidth label={t('common.country')} value={customer.country}
                onChange={e => setCustomer({ ...customer, country: e.target.value })} disabled={loading} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField fullWidth type="number" label={t('customers.credit_limit')} value={customer.creditLimit}
                onChange={e => setCustomer({ ...customer, creditLimit: Number(e.target.value) })} disabled={loading} inputProps={{ step: '0.01', min: '0' }} />
              <FormControlLabel control={<Switch checked={customer.isActive} onChange={e => setCustomer({ ...customer, isActive: e.target.checked })} disabled={loading} />}
                label={t('common.active')} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button type="submit" variant="contained" startIcon={<Save />} disabled={loading}>{loading ? t('common.saving') : t('common.save')}</Button>
              <Button variant="outlined" startIcon={<Cancel />} onClick={() => navigate('/customers')} disabled={loading}>{t('common.cancel')}</Button>
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerForm;
